import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Select, MenuItem, FormControl, InputLabel,
    Grid, Typography, Box, Radio, RadioGroup, FormControlLabel
} from '@mui/material';

const imageTypes = ['avatar', 'banner', 'thumbnail'];
const imageRatios = ['1:1', '3:4', '1:2'];

export default function MediaSelectDialog({ open, onClose, onSubmit, selectedEntertainmentEntity }) {

    //console.log("selectedEntertainmentEntity : ", selectedEntertainmentEntity)
    const [mediaType, setMediaType] = useState('');
    const [imageType, setImageType] = useState('');
    const [aspectRatio, setAspectRatio] = useState('');
    const [selectedUrl, setSelectedUrl] = useState(null);
    const [mediaList, setMediaList] = useState([]);

    useEffect(() => {
        if (selectedEntertainmentEntity?.multimedia) {
            const initializedMedia = selectedEntertainmentEntity.multimedia.map(item => ({
                ...item,
                isSelected: false
            }));
            setMediaList(initializedMedia);
        }
    }, [selectedEntertainmentEntity]);

    const moveSelectedToTop = (multimedia) => {
        const selectedIndex = multimedia?.findIndex(item => item.isSelected);
        if (selectedIndex > 0) {
            const selectedItem = multimedia[selectedIndex];
            const rest = multimedia.filter((_, idx) => idx !== selectedIndex);
            return [selectedItem, ...rest];
        }
        return multimedia; // no change if already at top or not found
    };

    const handleSubmit = () => {
        const selectedMedia = mediaList.find(item => item.isSelected);
        const multimedia = moveSelectedToTop(mediaList);
        onSubmit?.(multimedia);
        onClose();
    };

    const filteredMedia = mediaList?.filter(media => {
        if (mediaType && media.mediaType !== mediaType) return false;
        if (mediaType === 'image') {
            if (imageType && media.imageType !== imageType) return false;
            if (aspectRatio && media.aspectRatio !== aspectRatio) return false;
        }
        return true;
    });

    const handleSelect = (index) => {
        const updated = mediaList.map((item, i) => ({
            ...item,
            isSelected: i === index // single-select logic
        }));
        setMediaList(updated);

        const selected = updated.find((item, i) => i === index);
        setSelectedUrl(selected.url);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Select Media</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Media Type</InputLabel>
                        <Select
                            value={mediaType}
                            label="Media Type"
                            onChange={(e) => {
                                setMediaType(e.target.value);
                                setImageType('');
                                setAspectRatio('');
                                setSelectedUrl(null);
                            }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="image">Image</MenuItem>
                            <MenuItem value="video">Video</MenuItem>
                            <MenuItem value="audio">Audio</MenuItem>
                        </Select>
                    </FormControl>

                    {mediaType === 'image' && (
                        <FormControl fullWidth>
                            <InputLabel>Image Type</InputLabel>
                            <Select
                                value={imageType}
                                label="Image Type"
                                onChange={(e) => {
                                    setImageType(e.target.value);
                                    setSelectedUrl(null);
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {imageTypes.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>

                {mediaType === 'image' && (
                    <FormControl sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Aspect Ratio</Typography>
                        <RadioGroup
                            row
                            value={aspectRatio}
                            onChange={(e) => {
                                setAspectRatio(e.target.value);
                                setSelectedUrl(null);
                            }}
                        >
                            {imageRatios.map(r => (
                                <FormControlLabel key={r} value={r} control={<Radio />} label={r} />
                            ))}
                        </RadioGroup>
                    </FormControl>
                )}

                <Grid container spacing={2}>
                    {filteredMedia.map((media, index) => {
                        const isSelected = media.isSelected || false;

                        return (
                            <Grid key={`${media.url}-${index}`}>
                                <Box
                                    onClick={() => handleSelect(mediaList.indexOf(media))}
                                    sx={{
                                        border: isSelected ? '2px solid #1976d2' : '1px solid #ccc',
                                        borderRadius: 2,
                                        padding: 1,
                                        cursor: 'pointer',
                                        transition: 'border 0.2s',
                                        backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                    }}
                                >
                                    {media.mediaType === 'image' && (
                                        <img
                                            src={media.url}
                                            alt={media.imageType}
                                            style={{ width: '100%', borderRadius: 8 }}
                                        />
                                    )}
                                    {media.mediaType === 'video' && (
                                        <video controls width="100%">
                                            <source src={media.url} type="video/mp4" />
                                        </video>
                                    )}
                                    {media.mediaType === 'audio' && (
                                        <audio controls style={{ width: '100%' }}>
                                            <source src={media.url} type="audio/mp3" />
                                        </audio>
                                    )}
                                    <Typography variant="caption" display="block" align="center" mt={1}>
                                        {media.imageType || media.mediaType} {media.aspectRatio && `(${media.aspectRatio})`}
                                    </Typography>
                                </Box>
                            </Grid>
                        );
                    })}

                </Grid>

            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={!mediaList.some(m => m.isSelected)}>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
}
